import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarPlus, CalendarDays, Check, X, AlertCircle, Clock, CheckCircle2, XCircle, Ban, UserCheck, Users } from 'lucide-react'
import { apiJson, type Page } from '../api'
import { canDecideLeave, getUser } from '../auth'
import Modal from '../components/Modal'
import Pager from '../components/Pager'
import SortableTh from '../components/SortableTh'

interface LeaveRequest {
  id: string
  requesterUsername: string
  requesterName: string
  leaveType: string
  startDate: string
  endDate: string
  days: number
  reason?: string
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  currentStep: 'manager' | 'hr' | 'done'
  managerUsername?: string | null
  managerDecidedBy?: string | null
  managerComment?: string | null
  managerDecidedAt?: string | null
  approverUsername?: string
  approverComment?: string
  decidedAt?: string
  createdAt: string
}

interface LeaveBalance {
  leaveType: string
  entitled: number | null
  used: number
  remaining: number | null
}

const statusMeta: Record<string, { label: string; cls: string; icon: typeof Clock }> = {
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 ring-amber-100', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100', icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700 ring-red-100', icon: XCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-500 ring-slate-200', icon: Ban },
}

function StatusBadge({ status }: { status: string }) {
  const meta = statusMeta[status] ?? statusMeta.pending
  const Icon = meta.icon
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${meta.cls}`}>
      <Icon size={12} />
      {meta.label}
    </span>
  )
}

/** Which approval step a pending request is waiting on. */
function StepChip({ r }: { r: LeaveRequest }) {
  if (r.status !== 'pending') return null
  if (r.currentStep === 'manager') {
    return (
      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-100">
        <UserCheck size={11} /> Manager review{r.managerUsername ? ` · ${r.managerUsername}` : ''}
      </span>
    )
  }
  return (
    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-violet-100">
      <Users size={11} /> HR review
    </span>
  )
}

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

function fmtDate(iso: string) {
  return (iso ?? '').slice(0, 10)
}

export default function Leave() {
  const navigate = useNavigate()
  const allowDecide = canDecideLeave()
  const me = getUser()?.username ?? ''
  const [page_data, setPageData] = useState<Page<LeaveRequest> | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [managerPending, setManagerPending] = useState(0)
  const [isManager, setIsManager] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [tab, setTab] = useState<'mine' | 'approvals' | 'team'>('mine')
  const [decision, setDecision] = useState<LeaveRequest | null>(null)
  const [decisionMode, setDecisionMode] = useState<'hr' | 'manager'>('hr')
  const [comment, setComment] = useState('')
  const [deciding, setDeciding] = useState(false)
  const [balances, setBalances] = useState<LeaveBalance[]>([])

  // list controls (server-side)
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)
  const [sort, setSort] = useState('createdAt')
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC')

  const scope = tab === 'team' ? 'manager' : tab === 'approvals' && allowDecide ? 'others' : 'mine'

  const handleSort = (field: string) => {
    if (sort === field) {
      setOrder(order === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSort(field)
      setOrder('ASC')
    }
    setPage(1)
  }

  const switchTab = (t: 'mine' | 'approvals' | 'team') => {
    setTab(t)
    setPage(1)
    setStatusFilter('')
  }

  // lightweight badge count: pending requests awaiting my HR decision
  const fetchPendingCount = useCallback(async () => {
    if (!allowDecide) return
    try {
      const data = await apiJson<Page<LeaveRequest>>('/leave-requests?scope=others&status=pending&size=1')
      setPendingCount(data.total)
    } catch {
      // badge is best-effort
    }
  }, [allowDecide])

  // manager queue: am I anyone's manager, and how many wait on my step?
  const fetchManagerQueue = useCallback(async () => {
    try {
      const [any, pending] = await Promise.all([
        apiJson<Page<LeaveRequest>>('/leave-requests?scope=manager&size=1'),
        apiJson<Page<LeaveRequest>>('/leave-requests?scope=manager&status=pending&size=100'),
      ])
      setIsManager(any.total > 0)
      setManagerPending(pending.data.filter((r) => r.currentStep === 'manager').length)
    } catch {
      // badge is best-effort
    }
  }, [])

  const fetchRequests = useCallback(async () => {
    setRefreshing(true)
    try {
      const params = new URLSearchParams({ scope, page: String(page), size: String(size), sort, order })
      if (statusFilter) params.set('status', statusFilter)
      const data = await apiJson<Page<LeaveRequest>>(`/leave-requests?${params}`)
      setPageData(data)
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load leave requests')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [scope, page, size, sort, order, statusFilter])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  useEffect(() => {
    fetchPendingCount()
  }, [fetchPendingCount])

  useEffect(() => {
    fetchManagerQueue()
  }, [fetchManagerQueue])

  const fetchBalances = useCallback(async () => {
    try {
      setBalances(await apiJson<LeaveBalance[]>('/leave-balances/me'))
    } catch {
      // balances are best-effort
    }
  }, [])

  useEffect(() => {
    fetchBalances()
  }, [fetchBalances])

  const requests = useMemo(() => page_data?.data ?? [], [page_data])
  const teamCols = (tab === 'approvals' && allowDecide) || tab === 'team'

  const decide = async (approved: boolean) => {
    if (!decision) return
    setDeciding(true)
    setActionError('')
    try {
      const endpoint =
        decisionMode === 'manager'
          ? `/leave-requests/${decision.id}/${approved ? 'manager-approve' : 'manager-reject'}`
          : `/leave-requests/${decision.id}/${approved ? 'approve' : 'reject'}`
      await apiJson(endpoint, {
        method: 'POST',
        body: JSON.stringify(comment.trim() ? { comment: comment.trim() } : {}),
        headers: { 'Content-Type': 'application/json' },
      })
      setDecision(null)
      setComment('')
      await fetchRequests()
      await fetchPendingCount()
      await fetchManagerQueue()
      await fetchBalances()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Decision failed')
    } finally {
      setDeciding(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Balance cards */}
      {balances.length > 0 && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {balances.map((b) => {
            const pct = b.entitled ? Math.min(100, (b.used / b.entitled) * 100) : 0
            return (
              <div key={b.leaveType} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{b.leaveType}</p>
                {b.remaining === null ? (
                  <p className="mt-1.5 text-xl font-extrabold tracking-tight text-slate-400">Untracked</p>
                ) : (
                  <p className="mt-1.5 text-xl font-extrabold tracking-tight">
                    {b.remaining}
                    <span className="ml-1 text-xs font-medium text-slate-400">days left</span>
                  </p>
                )}
                <p className="mt-0.5 text-xs text-slate-400">
                  {b.entitled === null ? `${b.used} day(s) taken` : `${b.used} of ${b.entitled} used`}
                </p>
                {b.entitled !== null && (
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full transition-all ${pct >= 90 ? 'bg-red-400' : pct >= 70 ? 'bg-amber-400' : 'bg-brand-500'}`}
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
            <button
              onClick={() => switchTab('mine')}
              className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${
                tab === 'mine' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              My requests
            </button>
            {allowDecide && (
              <button
                onClick={() => switchTab('approvals')}
                className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${
                  tab === 'approvals' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Approvals
                {pendingCount > 0 && (
                  <span className="ml-1.5 rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {pendingCount}
                  </span>
                )}
              </button>
            )}
            {isManager && (
              <button
                onClick={() => switchTab('team')}
                className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${
                  tab === 'team' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Team approvals
                {managerPending > 0 && (
                  <span className="ml-1.5 rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {managerPending}
                  </span>
                )}
              </button>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
          >
            <option value="">All statuses</option>
            {Object.keys(statusMeta).map((s) => (
              <option key={s} value={s}>{statusMeta[s].label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => navigate('/leave/request')}
          className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:brightness-110"
        >
          <CalendarPlus size={16} />
          Request Leave
        </button>
      </div>

      {(loadError || actionError) && !decision && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {loadError || actionError}
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className={`overflow-x-auto transition-opacity ${refreshing ? 'opacity-60' : ''}`}>
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                {teamCols && (
                  <SortableTh label="Requester" field="requesterName" sort={sort} order={order} onSort={handleSort} />
                )}
                <SortableTh label="Type" field="leaveType" sort={sort} order={order} onSort={handleSort} />
                <SortableTh label="Dates" field="startDate" sort={sort} order={order} onSort={handleSort} />
                <SortableTh label="Days" field="days" sort={sort} order={order} onSort={handleSort} />
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Reason</th>
                <SortableTh label="Status" field="status" sort={sort} order={order} onSort={handleSort} />
                {teamCols && (
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Action</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {requests.map((r) => (
                <tr key={r.id} className="transition-colors hover:bg-slate-50/70">
                  {teamCols && (
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-semibold">{r.requesterName}</p>
                      <p className="text-xs text-slate-400">{r.requesterUsername}</p>
                    </td>
                  )}
                  <td className="px-5 py-3.5">
                    <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium capitalize text-slate-600">
                      {r.leaveType}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-500">
                    {fmtDate(r.startDate)} → {fmtDate(r.endDate)}
                  </td>
                  <td className="px-5 py-3.5 text-sm font-semibold">{r.days}</td>
                  <td className="max-w-[220px] px-5 py-3.5">
                    <p className="truncate text-sm text-slate-500" title={r.reason ?? ''}>
                      {r.reason || '—'}
                    </p>
                    {r.managerComment && (
                      <p className="mt-0.5 truncate text-xs text-indigo-400" title={r.managerComment}>
                        {r.managerDecidedBy} (manager): {r.managerComment}
                      </p>
                    )}
                    {r.approverComment && (
                      <p className="mt-0.5 truncate text-xs text-slate-400" title={r.approverComment}>
                        {r.approverUsername}: {r.approverComment}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusBadge status={r.status} />
                    <div><StepChip r={r} /></div>
                  </td>
                  {teamCols && (
                    <td className="px-5 py-3.5 text-right">
                      {r.status === 'pending' ? (
                        tab === 'team' ? (
                          r.currentStep === 'manager' && r.managerUsername === me ? (
                            <button
                              onClick={() => {
                                setDecision(r)
                                setDecisionMode('manager')
                                setComment('')
                                setActionError('')
                              }}
                              className="rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100"
                            >
                              Review
                            </button>
                          ) : (
                            <span className="text-xs text-slate-300">with HR</span>
                          )
                        ) : r.currentStep === 'hr' ? (
                          <button
                            onClick={() => {
                              setDecision(r)
                              setDecisionMode('hr')
                              setComment('')
                              setActionError('')
                            }}
                            className="rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-100"
                          >
                            Review
                          </button>
                        ) : (
                          <span className="text-xs text-slate-300">with manager</span>
                        )
                      ) : (
                        <span className="text-xs text-slate-300">decided</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {requests.length === 0 && !loadError && (
          <div className="flex flex-col items-center py-14 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <CalendarDays size={22} />
            </div>
            <p className="mt-3 text-sm font-semibold">
              {tab === 'mine' ? 'No leave requests yet' : tab === 'team' ? 'No team requests' : 'Nothing to review'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {tab === 'mine'
                ? 'Submit your first leave request to get started.'
                : tab === 'team'
                  ? 'Requests from your direct reports will appear here for manager review.'
                  : 'Pending requests from your team will appear here.'}
            </p>
          </div>
        )}

        <Pager
          page={page}
          totalPages={page_data?.totalPages ?? 1}
          total={page_data?.total ?? 0}
          size={size}
          onPage={setPage}
          onSize={(s) => { setSize(s); setPage(1) }}
        />
      </div>

      {/* Decision modal */}
      {decision && (
        <Modal
          title={`${decisionMode === 'manager' ? 'Manager review' : 'Review'} — ${decision.requesterName}`}
          subtitle={`${decision.leaveType} leave · ${fmtDate(decision.startDate)} → ${fmtDate(decision.endDate)} (${decision.days} day${decision.days === 1 ? '' : 's'})${decisionMode === 'manager' ? ' · approving forwards the request to HR' : ''}`}
          onClose={() => setDecision(null)}
        >
          <div className="space-y-4">
            {decision.reason && (
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                “{decision.reason}”
              </div>
            )}
            <div>
              <label className={labelCls}>Comment (optional, visible to the requester)</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Reason for the decision…"
                className={`${inputCls} resize-none`}
              />
            </div>

            {actionError && (
              <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle size={16} className="shrink-0" />
                {actionError}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button
                onClick={() => decide(false)}
                disabled={deciding}
                className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
              >
                <X size={15} />
                Reject
              </button>
              <button
                onClick={() => decide(true)}
                disabled={deciding}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition-all hover:brightness-110 disabled:opacity-50"
              >
                <Check size={15} />
                {deciding ? 'Saving…' : decisionMode === 'manager' ? 'Approve & forward to HR' : 'Approve'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
